import { Box, Link, MenuItem, ListItemIcon, ListItemText } from "@mui/material";
import Head from "next/head";
import { TbShirtSport } from "react-icons/tb";
import { HiOutlineTicket } from "react-icons/hi";
import { OpenInNewRounded } from "@mui/icons-material";
import { MdOutlineEventSeat, MdOutlinedFlag } from "react-icons/md";

import constants from "@/global/config/constants";

import type { TeamConfig } from "@/global/hooks/useTeamConfig";

export const howItWorks = [
  {
    icon: <MdOutlinedFlag />,
    title: "Choose Your Games ",
    description: "Select the games you wish to attend with your group.",
  },
  {
    icon: <TbShirtSport />,
    title: "Login to Account Manager",
    description: (
      <>
        Click Continue and connect to your{" "}
        <Link
          href={"https://am.ticketmaster.com/tbjrc/"}
          target={"_blank"}
          sx={{
            color: "primary.main",
            textDecoration: "none",
            fontWeight: 500,
          }}
        >
          Toronto Blue Jays
        </Link>{" "}
        account using our secure Ticketmaster login.
      </>
    ),
  },
  {
    icon: <MdOutlineEventSeat />,
    title: "Select Your Seats",
    description: "Select the seats you wish to purchase for your group.",
  },
  {
    icon: <HiOutlineTicket />,
    title: "Get Your Tickets",
    description: (
      <>
        Tickets will be available to be managed closer to the start of the{" "}
        <b>2027</b> season. You will receive email notification once they are
        made active. Reach out to Ticket Sales & Service at{" "}
        <Link
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
          href="tel:4163411234"
        >
          (416) 341-1234
        </Link>{" "}
        for any questions.
      </>
    ),
  },
];

export const faqs = [
  {
    title: "General Information",
    description: (
      <>
        Blue Jays Group Tickets Packages start at 12 tickets for one game, all
        within the same group price level. All group orders under 25 tickets
        must be purchased in one transaction.
        <br />
        <br />
        Please note that the seats available for Group Tickets Packages are
        limited in quantity and specific seat locations are subject to
        availability.
      </>
    ),
  },
  {
    title: "Group Tickets Packages Eligibility",
    description: (
      <>
        To be eligible to purchase a Group Tickets Package, all tickets must be
        purchased for use by members of a group comprised entirely of
        individuals who are closely connected by pre-existing relationships or
        shared membership in a specific organization or activity, as determined
        by the Toronto Blue Jays in its sole discretion. Examples of eligible
        groups include employees of a single organization, members of a sports
        team (e.g., players, coaches, and parents), students and teachers from a
        single school, a group of family members, and a group of friends. To
        find out if your group is eligible, please contact our Ticketing team by
        calling{" "}
        <Link
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
          href="tel:4163411234"
        >
          416-341-1234
        </Link>
        .
        <br />
        <br />
        Any purchase of a Group Tickets Package for purposes other than for use
        by members of an eligible group is strictly prohibited and the Toronto
        Blue Jays reserve the right to cancel any tickets purchased as part of
        such Group Tickets Package. In addition, any actual use of any tickets
        purchased as part of a Group Tickets Package by persons other than the
        members of an eligible group is strictly prohibited and the Toronto Blue
        Jays reserve the right to cancel any tickets purchased as part of such
        Group Tickets Package. Finally, any resale of, or offer to resell, any
        tickets purchased as part of a Group Tickets Package on a secondary
        market (e.g., StubHub, Kijiji, etc.) is strictly prohibited and the
        Toronto Blue Jays reserve the right to cancel any tickets purchased as
        part of such Group Tickets Package.
        <br />
        <br />
        If you are a Travel &amp; Tour operator, you are not eligible to
        purchase a Group Tickets Package, but please contact the Blue Jays
        Ticketing team at{" "}
        <Link
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
          href="tel:4163411234"
        >
          416-341-1234
        </Link>{" "}
        to learn more about our Ticket Purchase and Travel Package Agreement.
      </>
    ),
  },
  {
    title: "Game Selection",
    description: (
      <>
        A Groups Tickets Package can be purchased to all <b>2027</b> regular
        season games excluding Home Opener presented by TD, Canada Day, April
        22, and May 17.
        Please note that inventory is limited and subject to availability. The{" "}
        <i>Blue Jays</i> <b>2027</b> regular season schedule is available{" "}
        <Link
          target={"_blank"}
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
          href="https://www.mlb.com/bluejays/schedule/2027-03"
        >
          here
        </Link>
        .
      </>
    ),
  },
  {
    title: "Pricing",
    description:
      "Group Tickets Packages prices vary depending on the chosen game, the seating area in the stadium, the number of tickets in the package, and when tickets are purchased. You can design your Group Ticket Package to meet your party’s needs and book early to lock in the best price available!",
  },
  {
    title: "Payment",
    description: (
      <>
        If your Group Tickets Package consists of <b>12-24 tickets</b>, full
        payment is required at the time of purchase.
        <br />
        <br />
        Refunds and exchanges are not available on tickets purchased within a
        Group Tickets Package.
      </>
    ),
  },
  {
    title: "Online Ticket Management",
    description: (
      <>
        Purchasers of Group Tickets Packages will receive an invoice which has a
        link to our{" "}
        <Link
          target={"_blank"}
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
          href="https://am.ticketmaster.com/tbjrc/"
        >
          Blue Jays Account Manager
        </Link>{" "}
        website.
        <br />
        <br />
        For additional assistance accessing your account and managing your
        tickets, click{" "}
        <Link
          target={"_blank"}
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
          href="https://beta-gcp.mlb.com/bluejays/tickets/ticket-management"
        >
          here
        </Link>
        . You can also contact our Ticket Support Team at{" "}
        <Link
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
          href="tel:4163411234"
        >
          416-341-1234
        </Link>
        .
      </>
    ),
  },
  {
    title: "Bus Parking",
    description: (
      <>
        To book bus parking, you must already have purchased a Group Tickets
        Package. For further info, please contact our Group Sales Team at{" "}
        <Link
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
          href="tel:4163411234"
        >
          416-341-1234
        </Link>
        .
      </>
    ),
  },
  {
    title: "Accessible Parking",
    description: (
      <>
        Accessible parking spaces in the Rogers Centre underground parking lot
        can be purchased for any Blue Jays games, subject to availability. To
        reserve an accessible spot, please contact our Blue Jays Ticketing Team
        at{" "}
        <Link
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
          href="tel:4163411234"
        >
          416-341-1234
        </Link>
        . An Accessible Parking Permit is required to book an Accessible parking
        spot.
      </>
    ),
  },
];

const termsLinkSx = {
  fontWeight: 500,
  color: "primary.main",
  textDecoration: "none",
};

export const RegistrationTerms = () => (
  <>
    In connection with your registration, you understand that your personal
    information will be submitted to (i) Rogers Blue Jays Baseball Partnership
    (owner of the Toronto Blue Jays baseball team) ("<b>RBJBP</b>") and treated
    in accordance with the{" "}
    <Link
      target={"_blank"}
      sx={termsLinkSx}
      href="https://www.mlb.com/bluejays/team/rogers-blue-jays-baseball-partnership-privacy-policy?partnerID=redirect-tor-privacy"
    >
      RBJBP Privacy Policy
    </Link>
    , and MLB Advanced Media, L.P. ("<b>MLB</b>") and will be treated in
    accordance with the{" "}
    <Link
      target={"_blank"}
      sx={termsLinkSx}
      href="https://www.mlb.com/official-information/privacy-policy"
    >
      MLB Privacy Policy
    </Link>
    . You consent to the collection, use, and disclosure of my personal
    information in accordance with the foregoing privacy policies. In addition,
    by completing a transaction for a Toronto Blue Jays ticket or ticket
    product, you consent to the applicable{" "}
    <Link
      target={"_blank"}
      sx={termsLinkSx}
      href="https://www.mlb.com/bluejays/tickets/terms-and-conditions?partnerID=redirect-tor-ticketterms"
    >
      Toronto Blue Jays Terms and Conditions
    </Link>
    , as amended from time to time and the{" "}
    <Link
      target={"_blank"}
      sx={termsLinkSx}
      href="https://www.mlb.com/official-information/terms-of-use"
    >
      MLB Terms of Use
    </Link>
    .
  </>
);

export const CheckoutTerms = () => (
  <>
    <Box component="span" display="block">
      By completing your transaction, you agree and consent to the following:
    </Box>
    <Box component="span" display="block" mt={1}>
      The purchase and/or use of any <i>Toronto Blue Jays</i> Tickets or Ticket
      Products are subject to the applicable{" "}
      <Link
        target={"_blank"}
        sx={{ ...termsLinkSx, fontWeight: 700, fontStyle: "italic" }}
        href="https://www.mlb.com/bluejays/tickets/terms-and-conditions"
      >
        Toronto Blue Jays Ticket Terms and Conditions
      </Link>
      , as amended from time to time.
    </Box>
    <Box component="span" display="block" mt={1}>
      Your personal information will be submitted to: (i) Rogers Blue Jays
      Baseball Partnership (owner of the <i>Toronto Blue Jays</i> baseball team)
      ("<b>RBJBP</b>") and treated in accordance with the{" "}
      <Link
        target={"_blank"}
        sx={termsLinkSx}
        href="https://www.mlb.com/bluejays/team/rogers-blue-jays-baseball-partnership-privacy-policy?partnerID=redirect-tor-privacy"
      >
        RBJBP Privacy Policy
      </Link>
      ; and (ii) MLB Advanced Media, L.P. ("<b>MLB</b>") and treated in
      accordance with the{" "}
      <Link
        target={"_blank"}
        sx={termsLinkSx}
        href="https://www.mlb.com/official-information/privacy-policy"
      >
        MLB Privacy Policy
      </Link>
      .
    </Box>
  </>
);

export const TCListItems = () => (
  <Box>
    <MenuItem
      onClick={() => {
        window.open(
          "https://www.mlb.com/app/ballpark/official-information/privacy-policy",
          "_blank"
        );
      }}
    >
      <ListItemIcon>
        <OpenInNewRounded fontSize="small" />
      </ListItemIcon>
      <ListItemText>MLB Privacy Policy</ListItemText>
    </MenuItem>

    <MenuItem
      onClick={() => {
        window.open(
          "https://www.mlb.com/app/ballpark/official-information/terms-of-use",
          "_blank"
        );
      }}
    >
      <ListItemIcon>
        <OpenInNewRounded fontSize="small" />
      </ListItemIcon>
      <ListItemText>MLB Terms of Use</ListItemText>
    </MenuItem>
  </Box>
);

export const AnalyticsHeader = (props: { teamConfig: TeamConfig }) => {
  const { teamSchema } = props.teamConfig;

  return (
    <Head>
      <script
        nonce={process.env.pixelScriptNonce}
        id={`${teamSchema}-meta-pixel-script`}
        defer
        dangerouslySetInnerHTML={{
          __html: `
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '346053528923090');
              fbq('track', 'PageView');
              console.log('meta pixel page load event fired.');
              window.addEventListener("${constants.customEvents.eventPurchaseSuccess}", function(e) {
                try {
                  const { cartRes, checkoutRes } = e.detail;
                  fbq('track', 'Purchase', {
                    content_name: "${teamSchema}_event_purchase",
                    value: String(cartRes.allInPrice),
                    currency: 'USD',
                    order_id: checkoutRes.orderNumber,
                  });
                  console.log('meta pixel purchase event fired.');
                } catch (error) {
                  console.log('Error in meta pixel purchase event', error);
                }
              });
            `,
        }}
      />

      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          src="https://www.facebook.com/tr?id=346053528923090&ev=PageView&noscript=1"
        />
      </noscript>

      {/* Floodlight script */}
      <script
        nonce={process.env.googleTagManagerNonce}
        id={`${teamSchema}-floodlight-script`}
        defer
        dangerouslySetInnerHTML={{
          __html: `
            (function(){
              var FLOODLIGHT_SRC = '2946439';
              var FLOODLIGHT_TYPE_VIEW = 'torsa158';
              var FLOODLIGHT_CAT_VIEW = 'march000';
              var FLOODLIGHT_TYPE_PURCHASE = 'torsa158';
              var FLOODLIGHT_CAT_PURCHASE = 'march000';

              // Fire page view confirmation (image beacon)
              try {
                var ord = Math.floor(Math.random()*1e16);
                var img = new Image(1,1);
                img.style.display = 'none';
                img.alt = '${teamSchema} Floodlight View';
                img.src = 'https://ad.doubleclick.net/ddm/activity/src=' + FLOODLIGHT_SRC + ';type=' + FLOODLIGHT_TYPE_VIEW + ';cat=' + FLOODLIGHT_CAT_VIEW + ';ord=' + ord + '?';
                if (document.body) document.body.appendChild(img);
                console.log('Floodlight view ping fired.');
              } catch(e) { console.log('Floodlight view ping error', e); }

              // Purchase confirmation on app event
              window.addEventListener('${constants.customEvents.eventPurchaseSuccess}', function(e){
                try {
                  var cartRes = e && e.detail && e.detail.cartRes;
                  var checkoutRes = e && e.detail && e.detail.checkoutRes;
                  var ord = (checkoutRes && checkoutRes.orderNumber) || Math.floor(Math.random()*1e16);
                  var cost = cartRes && cartRes.allInPrice;
                  var qty = (cartRes && cartRes.totalSeats) || 1;
                  var url = 'https://ad.doubleclick.net/ddm/activity/src=' + FLOODLIGHT_SRC + ';type=' + FLOODLIGHT_TYPE_PURCHASE + ';cat=' + FLOODLIGHT_CAT_PURCHASE + ';ord=' + ord + ';qty=' + encodeURIComponent(String(qty)) + ';cost=' + encodeURIComponent(String(cost)) + '?';
                  var img = new Image(1,1);
                  img.style.display = 'none';
                  img.src = url;
                  img.alt = '${teamSchema} Floodlight Purchase';
                  if (document.body) document.body.appendChild(img);
                  console.log('Floodlight purchase ping fired.', { ord, cost, qty });
                } catch(err) {
                  console.log('Floodlight purchase ping error', err);
                }
              });
            })();
          `,
        }}
      />

      {/** tiktok script */}
      <script
        nonce={process.env.tiktokScriptNonce}
        defer
        id={`${teamSchema}-tik-tok-script`}
        dangerouslySetInnerHTML={{
          __html: `
              !(function (w, d, t) {
                w.TiktokAnalyticsObject = t;
                var ttq = (w[t] = w[t] || []);
                (ttq.methods = [
                  "page",
                  "track",
                  "identify",
                  "instances",
                  "debug",
                  "on",
                  "off",
                  "once",
                  "ready",
                  "alias",
                  "group",
                  "enableCookie",
                  "disableCookie",
                ]),
                  (ttq.setAndDefer = function (t, e) {
                    t[e] = function () {
                      t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
                    };
                  });
                for (var i = 0; i < ttq.methods.length; i++)
                  ttq.setAndDefer(ttq, ttq.methods[i]);
                (ttq.instance = function (t) {
                  for (var e = ttq._i[t] || [], n = 0; n < ttq.methods.length; n++)
                    ttq.setAndDefer(e, ttq.methods[n]);
                  return e;
                }),
                  (ttq.load = function (e, n) {
                    var i = "https://analytics.tiktok.com/i18n/pixel/events.js";
                    (ttq._i = ttq._i || {}),
                      (ttq._i[e] = []),
                      (ttq._i[e]._u = i),
                      (ttq._t = ttq._t || {}),
                      (ttq._t[e] = +new Date()),
                      (ttq._o = ttq._o || {}),
                      (ttq._o[e] = n || {});
                    n = document.createElement("script");
                    (n.type = "text/javascript"),
                      (n.async = !0),
                      (n.src = i + "?sdkid=" + e + "&lib=" + t);
                    e = document.getElementsByTagName("script")[0];
                    e.parentNode.insertBefore(n, e);
                  });

                ttq.load("CR7M1F3C77U1F4DL6V9G");
                ttq.page();
                console.log("tiktok pixel page view fired.");
              })(window, document, "ttq");

              window.addEventListener(
                "${constants.customEvents.eventPurchaseSuccess}",
                async function (e) {
                  var cartRes = e && e.detail && e.detail.cartRes;
                  var checkoutRes = e && e.detail && e.detail.checkoutRes;

                  ttq.identify({
                    email: e.detail.user.email,
                    phone_number: "+1" + e.detail.user.phoneNumber,
                  });

                  ttq.track("CompletePayment", {
                    content_id: checkoutRes.orderNumber,
                    content_name: "${teamSchema}_event_purchase",
                    quantity: String(cartRes.totalSeats),
                    price: String(cartRes.allInPrice),
                    value: String(cartRes.allInPrice),
                    currency: "USD",
                  });
                  console.log("tik tok pixel purchase event fired.");
                }
              );
            `,
        }}
      />
    </Head>
  );
};
